import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { format, subDays } from "date-fns";
import { apiClient } from "../../../zodios/client";
import type { UserActivityReport } from "../../../zodios/apiDefinitions/api/staffUserActivity";

export interface ActivityPeriod {
  start: string;
  end: string;
}

export function periodEndingToday(days: number, today: Date = new Date()): ActivityPeriod {
  return { start: format(subDays(today, days - 1), "yyyy-MM-dd"), end: format(today, "yyyy-MM-dd") };
}

/** How often to ask whether a running job has finished. Each poll answers at once from the API's cache. */
const POLL_INTERVAL_MS = 3000;

const pollWhileRunning = (query: { state: { data?: { status: string } } }) =>
  query.state.data?.status === "running" ? POLL_INTERVAL_MS : false;

const reportKey = (orgId: number, period: ActivityPeriod) => [{ entity: "staffUserActivity", orgId, ...period }];

/**
 * The report for a period, built by a background job the first request starts. While a job runs, the
 * last finished report for this org stays available, so switching period or refreshing doesn't blank
 * the page.
 */
export function useUserActivityReport(orgId: number, period: ActivityPeriod) {
  const query = useQuery({
    queryKey: reportKey(orgId, period),
    queryFn: async () => await apiClient.getStaffUserActivity({ params: { orgId }, queries: period }),
    refetchInterval: pollWhileRunning,
    refetchIntervalInBackground: true,
  });

  const ready = query.data?.status === "ready" ? query.data.result : undefined;
  const [lastReady, setLastReady] = useState<UserActivityReport | undefined>(undefined);
  useEffect(() => {
    if (ready) setLastReady(ready);
  }, [ready]);

  const shown = ready ?? (lastReady?.org.id === orgId ? lastReady : undefined);
  const job = query.data?.status === "ready" ? undefined : query.data;
  const isFailed = job?.status === "failed" || query.isError;

  return {
    report: shown,
    /** The report on screen is from an earlier request while a new one is being built. */
    isStale: !ready && !!shown,
    isRunning: !isFailed && (!query.data || job?.status === "running"),
    isFailed,
    error: query.error,
    startedAt: job?.started_at ?? undefined,
  };
}

/** Starts a fresh build, then lets the report query poll it like any other job. */
export function useRefreshUserActivity(orgId: number, period: ActivityPeriod) {
  const queryClient = useQueryClient();
  return async () => {
    const state = await apiClient.getStaffUserActivity({ params: { orgId }, queries: { ...period, refresh: "1" } });
    queryClient.setQueryData(reportKey(orgId, period), state);
  };
}

export function usePersonEvents(orgId: number, userId: number | null, period: ActivityPeriod) {
  const query = useQuery({
    queryKey: [{ entity: "staffUserActivityPerson", orgId, userId, ...period }],
    queryFn: async () =>
      await apiClient.getStaffUserActivityPerson({
        params: { orgId, userId: userId! },
        queries: period,
      }),
    enabled: userId !== null,
    refetchInterval: pollWhileRunning,
  });
  return {
    events: query.data?.status === "ready" ? query.data.result : undefined,
    isFailed: query.data?.status === "failed" || query.isError,
  };
}
